import { useMemo } from 'react';
import useUnitProvider from '/@/core/context/unit';
import { formatGeometry, type Geometry, type MultiPolygon } from '/@/core/fields/geometry';
import { isMultiPolygon, isPoint } from '/@/utils';

export const calculateArea = (geometry: Geometry) => {
    if (isPoint(geometry)) {
        return 0;
    }

    let area = 0;
    let geometryToCompute: MultiPolygon;
    if (!isMultiPolygon(geometry)) {
        geometryToCompute = [geometry] as MultiPolygon;
    } else {
        geometryToCompute = geometry;
    }

    geometryToCompute.forEach((polygon) => {
        const googlePolygon = new google.maps.Polygon({
            paths: formatGeometry(polygon),
        });

        // For polygons with holes, compute outer area and subtract hole areas
        const polygonPaths = googlePolygon.getPaths();
        if (polygonPaths && polygonPaths.getLength() > 0) {
            // Get outer ring (first path)
            const outerPath = polygonPaths.getAt(0);
            if (outerPath) {
                let polygonArea = google.maps.geometry.spherical.computeArea(outerPath);

                // Subtract area of each hole (subsequent paths)
                for (let i = 1; i < polygonPaths.getLength(); i++) {
                    const holePath = polygonPaths.getAt(i);
                    if (holePath) {
                        polygonArea -= google.maps.geometry.spherical.computeArea(holePath);
                    }
                }

                area += Math.max(0, polygonArea); // Ensure non-negative area
            }
        }
    });

    return area;
};

export const convertAreaFromM2 = (area: number, units: ReturnType<typeof useUnitProvider>) => {
    return units.toArea(units.clone({ area: 'm2' }).fromArea(area), 2);
};

export const convertAreaToM2 = (area: number, units: ReturnType<typeof useUnitProvider>) => {
    return units.clone({ area: 'm2' }).toArea(units.fromArea(area), 2);
};

/** Form planted area is in ha; FieldView reports are ha; field geometry area is m². */
export const plantedAreaForForm = (
    units: ReturnType<typeof useUnitProvider>,
    { reportHa, fieldM2 }: { reportHa?: number | null; fieldM2?: number | null }
): number | undefined => {
    if (reportHa != null) return reportHa;
    if (fieldM2 != null) return convertAreaFromM2(fieldM2, units).value;
    return undefined;
};

export const useCoordinatesArea = (coordinates: number[][]) => {
    const area = useMemo(() => {
        return calculateArea(coordinates);
    }, [coordinates]);

    const units = useUnitProvider();

    const areaLabel = convertAreaFromM2(area, units).toString();

    return { area, areaLabel };
};
